/**
 * Practice Mode screen. Two phases: a setup form (difficulty + question count)
 * and the active one-question-at-a-time flow with the recovery ladder. All
 * session state lives in a PracticeSession; this file only renders it and
 * dispatches user actions.
 */

import type { Screen } from "../router";
import type { Store } from "../state";
import { DIFFICULTY_LABELS } from "../state";
import { el, clear } from "../../ui/dom";
import { mathElement } from "../../ui/math-display";
import { createAnswerInput, type AnswerInput } from "../../ui/answer-input";
import { plotElement } from "../../graph/svg-render";
import { DIFFICULTIES, type Difficulty } from "../../topics/types";
import {
  getTopic,
  isTopicId,
  type TopicId,
} from "../../curriculum/curriculum";
import { hasGenerator } from "../../topics/registry";
import { createTopicSelect } from "../../ui/topic-select";
import { recommendedStartDifficulty } from "../../curriculum/calibration";
import { PracticeSession } from "../practice-session";
import { recordPracticeSummary } from "../../progress/record";

const COUNTS = [5, 10, 15, 20];

export const practiceScreen: Screen = (store, mount) => {
  const state = store.get();
  const paramTopic = state.screenParams["topic"];
  const topic: TopicId =
    typeof paramTopic === "string" && isTopicId(paramTopic)
      ? paramTopic
      : state.settings.currentTopic;

  if (!hasGenerator(topic)) {
    mount.appendChild(el("h1", {}, "Practice"));
    mount.appendChild(
      el("p", { class: "muted" }, `${getTopic(topic).name} is coming soon.`),
    );
    return;
  }

  renderSetup(store, mount, topic);
};

function renderSetup(store: Store, mount: HTMLElement, topic: TopicId): void {
  clear(mount);
  let difficulty: Difficulty = recommendedStartDifficulty(topic);
  let count = 10;

  mount.appendChild(el("h1", {}, "Practice"));

  // Topic selector (grouped by unit): change what you practice right here.
  const topicSelect = createTopicSelect({
    selected: topic,
    ariaLabel: "Practice topic",
    onChange: (value) => {
      if (value !== topic) {
        store.setCurrentTopic(value);
        renderSetup(store, mount, value);
      }
    },
  });
  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "Topic"),
      topicSelect,
    ),
  );

  // Difficulty chips.
  const diffChips = DIFFICULTIES.map((d) =>
    el(
      "button",
      {
        class: "chip",
        "aria-pressed": d === difficulty,
        onClick: (e) => {
          difficulty = d;
          syncPressed(e.currentTarget as HTMLElement, "difficulty");
        },
        "data-group": "difficulty",
        "data-value": d,
      },
      DIFFICULTY_LABELS[d],
    ),
  );

  // Count chips.
  const countChips = COUNTS.map((c) =>
    el(
      "button",
      {
        class: "chip",
        "aria-pressed": c === count,
        onClick: (e) => {
          count = c;
          syncPressed(e.currentTarget as HTMLElement, "count");
        },
        "data-group": "count",
        "data-value": String(c),
      },
      String(c),
    ),
  );

  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "Difficulty"),
      el("div", { class: "options" }, ...diffChips),
      el("p", { class: "card-title", style: "margin-top:1rem" }, "Number of Questions"),
      el("div", { class: "options" }, ...countChips),
      el(
        "div",
        { class: "btn-row" },
        el(
          "button",
          {
            class: "btn btn-primary",
            onClick: () => {
              const session = new PracticeSession({ topic, difficulty, count });
              renderActive(store, mount, session);
            },
          },
          "Start Practice",
        ),
      ),
    ),
  );
}

/** Toggle aria-pressed within a chip group (single-select). */
function syncPressed(clicked: HTMLElement, group: string): void {
  const root = clicked.closest("section");
  if (!root) return;
  root
    .querySelectorAll(`[data-group="${group}"]`)
    .forEach((n) => n.setAttribute("aria-pressed", "false"));
  clicked.setAttribute("aria-pressed", "true");
}

function renderActive(
  store: Store,
  mount: HTMLElement,
  session: PracticeSession,
): void {
  clear(mount);

  const meta = el("p", { class: "question-meta" });
  const promptWrap = el("div", { class: "question-prompt" });
  const inputHost = el("div", {});
  const feedback = el("div", { "aria-live": "polite" });
  const reveal = el("div", {});
  const controls = el("div", { class: "btn-row" });

  const card = el(
    "section",
    { class: "card" },
    meta,
    promptWrap,
    inputHost,
    feedback,
    reveal,
    controls,
  );

  mount.appendChild(el("h1", {}, "Practice"));
  mount.appendChild(card);

  // The active answer input, rebuilt per question (choice vs text).
  let answerInput: AnswerInput;

  const showFeedback = (correct: boolean): void => {
    clear(feedback);
    feedback.appendChild(
      el(
        "div",
        { class: `feedback ${correct ? "feedback-correct" : "feedback-incorrect"}` },
        el("span", { class: "feedback-icon", "aria-hidden": "true" }, correct ? "\u2713" : "\u2717"),
        el("span", {}, correct ? "Correct!" : "Not quite - try again, or use a hint."),
      ),
    );
  };

  const appendReveal = (title: string, description: string, latex?: string): void => {
    const block = el(
      "div",
      { class: "reveal-step" },
      el("strong", {}, title),
      el("div", {}, description),
    );
    if (latex) block.appendChild(mathElement(latex));
    if (!reveal.firstChild) reveal.className = "reveal";
    reveal.appendChild(block);
  };

  const advance = (): void => {
    if (session.isLast) {
      recordPracticeSummary(session);
      renderSummary(store, mount, session);
    } else {
      session.next();
      renderQuestion();
    }
  };

  const submit = (): void => {
    const o = session.current;
    if (o.correct || o.solutionRevealed) {
      advance();
      return;
    }
    const raw = answerInput.getValue();
    if (raw === "") return;
    const result = session.submit(raw);
    showFeedback(result.correct);
    if (result.correct) {
      answerInput.setEnabled(false);
    }
    buildControls();
  };

  function buildControls(): void {
    clear(controls);
    const o = session.current;

    if (o.correct || o.solutionRevealed) {
      controls.appendChild(
        el(
          "button",
          { class: "btn btn-primary", onClick: advance },
          session.isLast ? "See Results" : "Next Question",
        ),
      );
      return;
    }

    controls.appendChild(
      el("button", { class: "btn btn-primary", onClick: submit }, "Check Answer"),
    );

    if (session.hintsRemaining > 0) {
      controls.appendChild(
        el(
          "button",
          {
            class: "btn",
            onClick: () => {
              const hint = session.useHint();
              if (hint) appendReveal("Hint", hint);
              buildControls();
            },
          },
          "Hint",
        ),
      );
    }

    if (session.stepsRemaining > 0) {
      controls.appendChild(
        el(
          "button",
          {
            class: "btn",
            onClick: () => {
              const step = session.revealNextStep();
              if (step) appendReveal("Next step", step.description, step.latex);
              buildControls();
            },
          },
          "Show a step",
        ),
      );
    }

    controls.appendChild(
      el(
        "button",
        {
          class: "btn",
          onClick: () => {
            const steps = session.revealSolution();
            clear(reveal);
            reveal.className = "reveal";
            reveal.appendChild(el("strong", {}, "Full solution"));
            for (const s of steps) {
              const b = el("div", { class: "reveal-step" }, el("div", {}, s.description));
              b.appendChild(mathElement(s.latex));
              reveal.appendChild(b);
            }
            answerInput.setEnabled(false);
            buildControls();
          },
        },
        "Show full solution",
      ),
    );
  }

  const renderQuestion = (): void => {
    const o = session.current;
    meta.textContent = `Question ${session.position} of ${session.total}`;
    clear(promptWrap);
    promptWrap.appendChild(mathElement(o.question.prompt.latex ?? o.question.prompt.text));
    if (o.question.graph) promptWrap.appendChild(plotElement(o.question.graph));
    clear(feedback);
    clear(reveal);
    clear(inputHost);
    answerInput = createAnswerInput(o.question, {
      onSubmit: submit,
      placeholder: answerPlaceholder(session),
    });
    inputHost.appendChild(answerInput.element);
    buildControls();
    answerInput.focus();
  };

  renderQuestion();
}

function renderSummary(
  store: Store,
  mount: HTMLElement,
  session: PracticeSession,
): void {
  clear(mount);
  const sum = session.summary();
  const pct =
    sum.total > 0 ? Math.round((sum.correct / sum.total) * 100) : 0;

  mount.appendChild(el("h1", {}, "Practice Complete"));
  mount.appendChild(
    el(
      "section",
      { class: "card center" },
      el("div", { class: "stat-value" }, `${sum.correct} / ${sum.total}`),
      el("div", { class: "muted" }, `${pct}% correct`),
    ),
  );

  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el(
        "div",
        { class: "stat-grid" },
        stat("First-Try", `${sum.firstAttempt}`),
        stat("Hints Used", `${sum.hintsUsed}`),
        stat("Solutions Shown", `${sum.solutionsRevealed}`),
      ),
    ),
  );

  mount.appendChild(
    el(
      "div",
      { class: "btn-row" },
      el(
        "button",
        {
          class: "btn btn-primary",
          onClick: () =>
            renderSetup(store, mount, session.config.topic),
        },
        "Practice Again",
      ),
      el(
        "button",
        { class: "btn", onClick: () => store.navigate("home") },
        "Back to Home",
      ),
    ),
  );
}

function stat(label: string, value: string): HTMLElement {
  return el(
    "div",
    { class: "stat" },
    el("div", { class: "stat-value" }, value),
    el("div", { class: "stat-label" }, label),
  );
}

/** Placeholder text hinting at the expected answer form for the topic. */
function answerPlaceholder(session: PracticeSession): string {
  const t = session.current.question.answerType;
  switch (t) {
    case "number":
      return "e.g. 6 or -3/4";
    case "decimal":
      return "e.g. 3.25";
    case "inequality":
      return "e.g. x < 4";
    case "compound":
      return "e.g. -3 < x <= 7";
    case "special":
      return "no solution / infinitely many";
    default:
      return "your answer";
  }
}
